import React, { useState, useCallback } from 'react';
import { UseFormReturn, FieldErrors, useForm } from 'react-hook-form';
import { useLocale } from 'next-intl';
import { Form, Page } from '@/payload/payload-types';
import { FormFieldBlock } from '@payloadcms/plugin-form-builder/types';
import { fields } from './fields';
import RichText from '../RichText';
import { Button, ButtonAppearance } from '../Button';
import { buildInitialFormState } from './buildInitialFormState';
import { useConfig } from '@/app/_providers/Config';
import { Captcha } from './Captcha';
import { Honeypot } from './Honeypot';

import classes from './FormItem.module.scss';

type SubmitButtonColor = 'gold' | 'grey' | 'green';

const buttonColorToAppearance: Record<SubmitButtonColor, ButtonAppearance> = {
  gold: 'primary',
  grey: 'secondary',
  green: 'accent',
};

type FormItemProps = {
  formID: string;
  formFromProps: Form;
  formFields: FormFieldBlock[];
  confirmationType?: string;
  confirmationMessage?: any;
  introContent?: any;
  submitButtonLabel?: string;
  submitButtonColor?: SubmitButtonColor | null;
  redirect?: {
    type?: ('reference' | 'custom') | null;
    reference?: {
      relationTo: 'pages';
      value: number | Page;
    } | null;
  };
};

export type Value = unknown;

export interface Property {
  [key: string]: Value;
}

export interface Data {
  [key: string]: Value | Property | Property[];
}


export const FormItem: React.FC<FormItemProps> = ({
  formID,
  formFromProps,
  formFields,
  confirmationType,
  confirmationMessage,
  introContent,
  submitButtonLabel,
  submitButtonColor,
  redirect,
}) => {
  const { payloadUrl } = useConfig();
  const locale = useLocale();
  const formMethods = useForm({
    defaultValues: buildInitialFormState(formFields as FormFieldBlock[]),
  });

  const { register, handleSubmit, formState: { errors }, control, setValue } = formMethods;

  const [isLoading, setIsLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState<boolean>();
  const [error, setError] = useState<{ status?: string; message: string; } | undefined>();

  // CAPTCHA and Honeypot state (automatically added to all forms)
  const [captchaData, setCaptchaData] = useState<string | null>(null);
  const [honeypotValue, setHoneypotValue] = useState<string>('');

  const onSubmit = useCallback(
    (data: Data) => {
      let loadingTimerID: ReturnType<typeof setTimeout>;

      const submitForm = async () => {
        setError(undefined);

        // Check honeypot field - if filled, it's likely a bot
        if (honeypotValue && honeypotValue.trim() !== '') {
          console.warn('[Form] Honeypot field was filled - likely bot submission');
          setError({
            message: 'Invalid submission detected.',
          });
          return;
        }

        // Validate CAPTCHA
        if (!captchaData) {
          setError({
            message: 'Please complete the verification.',
          });
          return;
        }

        const dataToSend = Object.entries(data).map(([name, value]) => ({
          field: name,
          value,
        }));

        // delay loading indicator by 1s
        loadingTimerID = setTimeout(() => {
          setIsLoading(true);
        }, 1000);

        try {
          // Parse CAPTCHA data (stored as JSON string with {id, answer})
          let captchaId, captchaToken;
          if (captchaData) {
            try {
              const parsed = JSON.parse(captchaData);
              captchaId = parsed.id;
              captchaToken = parsed.answer;
            } catch (e) {
              console.error('[Form] Failed to parse CAPTCHA data:', e);
            }
          }

          const req = await fetch(`${payloadUrl}/api/form-submissions/?locale=${locale}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              form: formID,
              submissionData: dataToSend,
              captchaToken,
              captchaId,
              honeypot: honeypotValue,
            }),
          });

          const res = await req.json();

          clearTimeout(loadingTimerID);

          if (req.status >= 400) {
            setIsLoading(false);
            setError({
              status: res.status,
              message: res.errors?.[0]?.message || 'Internal Server Error',
            });

            return;
          }

          setIsLoading(false);
          setHasSubmitted(true);

          if (confirmationType === 'redirect' && redirect) {
            const { url } = redirect as { url: string; };

            const redirectUrl = url;

            if (redirectUrl) {
              window.location.href = redirectUrl;
            }
          }
        } catch (err) {
          console.warn(err);
          setIsLoading(false);
          setError({
            message: 'Something went wrong.',
          });
        }
      };

      submitForm();
    },
    [formID, redirect, confirmationType, payloadUrl, captchaData, honeypotValue],
  );

  return (
    <div className={[classes.form, hasSubmitted && classes.hasSubmitted].filter(Boolean).join(' ')}>
      {!isLoading && hasSubmitted && confirmationType === 'message' && (
        <RichText className={classes.confirmationMessage} content={confirmationMessage} />
      )}
      {isLoading && !hasSubmitted && <p>Loading, please wait...</p>}
      {error && <div>{`${error.status || '500'}: ${error.message || ''}`}</div>}
      {!hasSubmitted && (
        <form id={`form-${formID}`} onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(onSubmit)();
        }}>
          <div className={classes.fieldWrap}>
            {formFields &&
              formFields.map((field, index) => {
                const Field: React.FC<any> = fields?.[field.blockType];
                if (Field) {
                  return (
                    <React.Fragment key={index}>
                      <Field
                        form={formFromProps}
                        {...field}
                        {...formMethods}
                        register={register}
                        errors={errors}
                        control={control}
                      />
                    </React.Fragment>
                  );
                }
                return null;
              })}
          </div>

          {/* Automatically included bot protection */}
          <Honeypot
            name="website_url"
            register={register}
            onChange={(e) => setHoneypotValue(e.target.value)}
          />

          <Captcha
            name="captcha_verification"
            label="Verify you are human"
            register={register}
            setValue={(_, value) => setCaptchaData(value as string)}
            errors={errors}
          />

          <div className={classes.formButtonContainer}>
            <div className={classes.formTextContent}>
              <RichText content={introContent} />
            </div>
            <div className={classes.formButtonWrapper}>
              <Button
                label={submitButtonLabel}
                appearance={buttonColorToAppearance[submitButtonColor || 'green']}
                form={`form-${formID}`}
                type="submit"
                width="100%"
              />
            </div>
          </div>
        </form>
      )}
    </div>
  );
};
